/**
 * Staff access approval (flag identity.staff_access_approval; docs/runbooks/staff-access-approval.md).
 * "Request access" to one workspace or agency (prefilled from the `requestPath` a 403 access_approval_required carries:
 * /access?tenantId=..|partnerId=..&scope=..), "My requests and grants" with cancel, and the platform-owner check.
 */
import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { Button, Heading, Panel, Text } from '@qm/ui';
import { useSurface } from '../kit/context';
import { useAction, useResource } from '../kit/resource';
import { Badge, DataTable, ErrorNote, Field, Loadable, Notice, Screen, When, reasonValid, type Column, type Tone } from '../kit/ui';
import type { AccessScope, AccessStatus, MyAccessRequest } from '../api';
import { useAdminApi } from './layout';

export const ACCESS_SCOPES: readonly AccessScope[] = ['read', 'write', 'view_as'];
export const MAX_HOURS = 168;
export const DEFAULT_HOURS = 24;

export interface AccessForm { targetType: 'tenant' | 'partner'; id: string; scope: AccessScope; reason: string; durationHours: number }

/** Form values from the query string a refusal links to; anything unexpected falls back to the defaults. */
export function prefillFromQuery(q: URLSearchParams): AccessForm {
  const partnerId = q.get('partnerId')?.trim() ?? '';
  const tenantId = q.get('tenantId')?.trim() ?? '';
  const scope = q.get('scope') as AccessScope | null;
  return {
    targetType: partnerId && !tenantId ? 'partner' : 'tenant',
    id: (partnerId && !tenantId ? partnerId : tenantId).slice(0, 80),
    scope: scope && ACCESS_SCOPES.includes(scope) ? scope : 'read',
    reason: '',
    durationHours: DEFAULT_HOURS,
  };
}

export const durationValid = (h: number) => Number.isInteger(h) && h >= 1 && h <= MAX_HOURS;
export const accessFormValid = (f: AccessForm) => f.id.trim().length >= 3 && reasonValid(f.reason) && durationValid(f.durationHours);

export function toRequestBody(f: AccessForm) {
  const target = f.targetType === 'partner' ? { partnerId: f.id.trim() } : { tenantId: f.id.trim() };
  return { ...target, scope: f.scope, reason: f.reason.trim(), durationHours: f.durationHours };
}

const STATUS_TONE: Record<string, Tone> = { pending: 'warn', active: 'good', declined: 'bad', revoked: 'bad', expired: 'neutral', lapsed: 'neutral', cancelled: 'neutral' };

export function AccessPage() {
  const { t } = useSurface();
  const api = useAdminApi();
  const status = useResource('identity:access-status', api.accessStatus);
  const mine = useResource('identity:access-requests', api.myAccessRequests);
  return (
    <Screen title={t('portal.admin.access.title')} subtitle={t('portal.admin.access.intro')} testId="admin-access">
      <Loadable resource={status}>{(s) => <AccessStatusLine status={s} />}</Loadable>
      <RequestAccessForm onDone={mine.reload} />
      <Panel>
        <Heading level={2}>{t('portal.admin.access.mine')}</Heading>
        <Loadable resource={mine}>{(d) => <MyGrants items={d.items} onChanged={mine.reload} />}</Loadable>
      </Panel>
      <PlatformOwnerCheck onDone={status.reload} />
    </Screen>
  );
}

export function AccessStatusLine({ status }: { status: AccessStatus }) {
  const { t } = useSurface();
  return (
    <Notice tone={status.enabled ? 'accent' : 'neutral'} testId="access-status">
      {t(status.enabled ? 'portal.admin.access.statusOn' : 'portal.admin.access.statusOff')}
      {status.platformOwner ? <> <Badge tone="good" testId="access-platform-owner">{t('portal.admin.access.platformOwner')}</Badge></> : null}
    </Notice>
  );
}

export function RequestAccessForm({ onDone }: { onDone: () => void }) {
  const { t } = useSurface();
  const api = useAdminApi();
  const [params] = useSearchParams();
  const [form, setForm] = useState<AccessForm>(() => prefillFromQuery(params));
  const act = useAction((f: AccessForm) => api.requestAccess(toRequestBody(f)));
  const set = <K extends keyof AccessForm>(k: K, v: AccessForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!accessFormValid(form)) return;
    if (await act.run(form)) { setForm((f) => ({ ...f, reason: '' })); onDone(); }
  };
  return (
    <Panel data-testid="access-request">
      <Heading level={2}>{t('portal.admin.access.request')}</Heading>
      <form onSubmit={(e) => void submit(e)} data-testid="access-request-form">
        <Field label={t('portal.admin.access.targetType')}>
          {(id) => (
            <select id={id} name="targetType" value={form.targetType} onChange={(e) => set('targetType', e.target.value === 'partner' ? 'partner' : 'tenant')}>
              <option value="tenant">{t('portal.admin.access.target.tenant')}</option>
              <option value="partner">{t('portal.admin.access.target.partner')}</option>
            </select>
          )}
        </Field>
        <Field label={t(form.targetType === 'partner' ? 'portal.admin.access.partnerId' : 'portal.admin.access.tenantId')} hint={t('portal.admin.access.idHint')}>
          {(id) => <input id={id} name="targetId" required minLength={3} maxLength={80} value={form.id} onChange={(e) => set('id', e.target.value)} />}
        </Field>
        <Field label={t('portal.admin.access.scope')}>
          {(id) => (
            <select id={id} name="scope" value={form.scope} onChange={(e) => set('scope', e.target.value as AccessScope)}>
              {ACCESS_SCOPES.map((s) => <option key={s} value={s}>{t(`portal.admin.access.scope.${s}`)}</option>)}
            </select>
          )}
        </Field>
        <Field label={t('portal.kit.reason')} hint={t('portal.admin.access.reasonHint')}>
          {(id) => <textarea id={id} name="reason" required minLength={5} maxLength={500} value={form.reason} onChange={(e) => set('reason', e.target.value)} />}
        </Field>
        <Field label={t('portal.admin.access.duration')} hint={t('portal.admin.access.durationHint', { max: MAX_HOURS })}>
          {(id) => <input id={id} name="durationHours" type="number" min={1} max={MAX_HOURS} step={1} value={form.durationHours} onChange={(e) => set('durationHours', Number(e.target.value))} />}
        </Field>
        <Button type="submit" variant="primary" disabled={act.pending || !accessFormValid(form)}>{t('portal.admin.access.send')}</Button>
      </form>
      {act.result ? <Notice tone="good" testId="access-request-sent">{t('portal.admin.access.sent')}</Notice> : null}
      <ErrorNote error={act.error} />
    </Panel>
  );
}

export function MyGrants({ items, onChanged }: { items: readonly MyAccessRequest[]; onChanged: () => void }) {
  const { t } = useSurface();
  const api = useAdminApi();
  const cancel = useAction(async (id: string) => { const r = await api.cancelAccessRequest(id); onChanged(); return r; });
  const columns: Column<MyAccessRequest>[] = [
    { key: 'target', header: t('portal.admin.access.col.target'), render: (r) => <><Text>{t(`portal.admin.access.target.${r.target.type}`)}</Text> <bdi>{r.target.id}</bdi></> },
    { key: 'scope', header: t('portal.admin.access.scope'), render: (r) => t(`portal.admin.access.scope.${r.scope}`) },
    { key: 'status', header: t('portal.admin.access.col.status'), render: (r) => <Badge tone={STATUS_TONE[r.status] ?? 'neutral'} testId={`access-status-${r.id}`}>{t(`portal.admin.access.status.${r.status}`)}</Badge> },
    { key: 'expires', header: t('portal.admin.access.col.expires'), render: (r) => <When iso={r.status === 'pending' ? r.requestExpiresAt : r.expiresAt} />, narrow: 'hide' },
    { key: 'reason', header: t('portal.kit.reason'), render: (r) => r.reason, narrow: 'hide' },
    {
      key: 'actions', header: t('portal.admin.access.col.action'), render: (r) => (r.status === 'pending' || r.status === 'active'
        ? <Button onClick={() => void cancel.run(r.id)} disabled={cancel.pending} data-testid={`access-cancel-${r.id}`}>{t(r.status === 'pending' ? 'portal.admin.access.withdraw' : 'portal.admin.access.giveUp')}</Button>
        : null),
    },
  ];
  return (
    <>
      <DataTable columns={columns} rows={items} rowKey={(r) => r.id} empty={t('portal.admin.access.none')} caption={t('portal.admin.access.mine')} />
      <ErrorNote error={cancel.error} />
    </>
  );
}

export function PlatformOwnerCheck({ onDone }: { onDone: () => void }) {
  const { t } = useSurface();
  const api = useAdminApi();
  const act = useAction(async () => { const r = await api.syncPlatformOwner(); onDone(); return r; });
  return (
    <Panel data-testid="platform-owner-check">
      <Heading level={2}>{t('portal.admin.access.ownerTitle')}</Heading>
      <Text tone="muted">{t('portal.admin.access.ownerIntro')}</Text>
      <Button onClick={() => void act.run()} disabled={act.pending} data-testid="platform-owner-sync">{t('portal.admin.access.ownerCheck')}</Button>
      {act.result ? (
        <Notice testId="platform-owner-result">
          {t('portal.admin.access.ownerResult', { owners: act.result.owners, granted: act.result.granted, revoked: act.result.revoked, awaiting: act.result.awaitingSignIn })}
        </Notice>
      ) : null}
      <ErrorNote error={act.error} />
    </Panel>
  );
}
